import type { AttachmentFit, AttachmentUse, Motion, TowerKit } from "../kit/types";

/**
 * Border Warden (Season 1, "The Border War": the War Campaign's Premium tier 30; docs/SHOP.md §15) — every tower a
 * frontier watch-fort (contract tools/art/skins/README.md; big kit, §11).
 *
 * The stock roof goes and a timber palisade crown under a tiered shingle watch-roof takes its place, a signal brazier
 * burning on its peak that flares with every shot (on the archer, poison and beacon the roof stands on four braced posts
 * so the bow, the brew and the orb stay in view; lightning keeps its roof and wears the palisade round its gallery; the
 * frost battlement becomes a stake crown round its crystal). A timber hoarding juts from every drum's top, teal border
 * banners hang from it and lift in the wind, a stake fence rings the plinth with a gate toward the camera where an alarm
 * bell swings in its frame, and two scouting hawks circle the tower, banking as they glide. The materials: weathered
 * border-stone, warm shingle, old timber, dark iron, teal cloth trimmed in bronze. In a level: signal-fire motes and teal
 * glints drift off the brazier, the bow-string winds up in amber sparks, and the hits alternate between a bronze spark
 * spray and a teal glint. Props: art/hq/skins/borderwarden/kit.glb (tools/art/skins/borderwarden.py).
 */
/** The roof storeys that hold the weapon or the element (bow, brew, the beacon's orb): the watch-roof stands on posts. */
const PAVILIONS = ["archer", "poison", "beacon"];
/** The closed roofs (the back roofs of the cannon, fire, ballista and mortar; the barracks hall). */
const CLOSED = ["cannon", "fire", "ballista", "mortar", "barracks"];
/** The beacon's orb stands in its roof's upper half: its watch-post rises this far, metres (as Bronze's pavilion). */
const BEACON_RISE = 0.27;
/** The fitted pieces stand this proud of the measured part (fraction of its radius). */
const ROOF_MARGIN = 0.06, FIRE_MARGIN = 0.35, CROWN_MARGIN = 0.08;
/** The fence stands on the plinth's top (metres above the plinth envelope's foot; the barracks' is a raised base). */
const PLINTH_TOP = 0.07, BARRACKS_PLINTH = 0.42;
/** The banners hang from under the hoarding's floor: out past its plank wall and up to its floor, metres. */
const BANNER_AT = [0, 0.05, 0.16] as const;
/** The slender shafts' banners: smaller, nearer the shaft. */
const SLIM: AttachmentFit = { scale: [0.9, 0.95, 1.0], offset: [0, 0.03, 0.13] };
/** The side faces are seen edge-on from the game camera: each side's banner turns this far toward it (degrees). */
const BANNER_TURN = 60;
const SIDE_BANNER: AttachmentUse = {
  piece: "banner", at: [], offset: BANNER_AT, scale: [1.3, 1.4, 1.5],
  motion: { wave: 0.022, waveK: 14, hz: 0.6, flap: { deg: 7, bias: -9, axis: "right", hz: 0.4 } },
  fit: { arcane: SLIM, hex: SLIM },
};
/** The bell frame and its bell share one scale, so the bell's pivot stays under the beam (0.243 m at scale 1). */
const BELL_SCALE = 1.3, BELL_PIVOT = 0.243 * BELL_SCALE;
/** The signal brazier on the watch-roof flickers, and flares as each shot nears. */
const BRAZIER_FIRE: Motion = { glow: 0.35, hz: 2.2, charge: { glow: 2.0 } };
/** The hawks: how far above the apex they circle, their orbit (m, rad/s) and their size. */
const HAWK = { lift: 0.42, radius: 0.5, speed: 0.55, copies: 2, scale: 1.8 } as const;

export const BORDERWARDEN: TowerKit = {
  id: "tower.borderwarden",
  asset: "skins/borderwarden/kit",
  hide: ["roof"],
  // the hex totem keeps its feather crown (its mask stands in it) and the arcane spire its collar (the crystal floats
  // on it): both wear the palisade crown on their sockets instead
  hideFit: { hex: [], arcane: [] },
  tier4: true,
  slots: {
    stone: { tint: 0x9d998a, mix: 0.85, gain: 1.05, roughness: 0.92, metalness: 0, keepAccents: false, pattern: { kind: "veins", colour: 0x30322c, scale: 4, strength: 0.7 } },
    roof: { tint: 0x9a6440, mix: 0.92, gain: 1.05, metalness: 0.05, roughness: 0.85, keepAccents: false, trim: { colour: 0xd39c58, strength: 0.5 }, pattern: { kind: "plates", colour: 0x2a1c10, scale: 11, strength: 0.6 } },
    wood: { tint: 0x8a6a48, mix: 0.85, gain: 1.05, roughness: 0.85, keepAccents: false, pattern: { kind: "grain", colour: 0x231910, scale: 10, strength: 0.5 } },
    metal: { tint: 0x2a2e30, mix: 0.9, metalness: 0.9, roughness: 0.4, keepAccents: false, trim: { colour: 0xd8a060, strength: 0.45 } },
    cloth: { tint: 0x17958a, mix: 0.95, gain: 1.2, roughness: 0.65, keepAccents: false, trim: { colour: 0xf0b860, strength: 0.7 } },
  },
  attachments: [
    // the watch-roof in the hidden roof's place: a palisade crown, three shingle courses, the signal brazier flaring on
    // each shot; the barracks' hall takes it boxed to its own ridge
    { piece: "watchroof", at: [], towers: CLOSED, ifHidden: "roof", fitTo: { to: "roof", height: "keep", margin: ROOF_MARGIN }, motion: BRAZIER_FIRE, fit: {
      barracks: { fitTo: { to: "roof", shape: "box", margin: 0.04 } },
      // the fire tower's chimney (its roof) is tall and slim: the top takes its full height, a little wider
      fire: { fitTo: { to: "roof", margin: FIRE_MARGIN } },
    } },
    { piece: "watchpost", at: [], towers: PAVILIONS, ifHidden: "roof", fitTo: { to: "roof", margin: ROOF_MARGIN }, motion: BRAZIER_FIRE, fit: { beacon: { offset: [0, BEACON_RISE, 0] } } },
    // the stake crown: round frost's battlement and lightning's gallery (under its kept roof), the arcane collar, the hex pole
    { piece: "palisade", at: [], towers: ["frost", "lightning", "arcane", "hex"], fitTo: { to: "gallery", height: "keep", margin: CROWN_MARGIN }, fit: {
      lightning: { scale: 0.85 },
      arcane: { fitTo: null, at: ["apex"], scale: [0.45, 0.48, 0.5] },
      hex: { fitTo: null, at: ["top"], scale: [0.36, 0.38, 0.4] },
    } },
    { piece: "fence", at: [], fitTo: { to: "plinth", height: "keep" }, offset: [0, PLINTH_TOP, 0], fit: { barracks: { offset: [0, BARRACKS_PLINTH, 0] } } },
    { piece: "hoarding", at: [], fitTo: { to: "drum", taper: true, margin: 0.02 } },
    // two scouting hawks circling high over the tower, banking as they glide
    { piece: "hawk", at: ["apex"], offset: [0, HAWK.lift, 0], scale: HAWK.scale,
      motion: { orbit: { radius: HAWK.radius / HAWK.scale, speed: HAWK.speed, copies: HAWK.copies, tilt: 6 }, sway: 7, hz: 0.18 } },
    // border banners hung from under the hoarding on the two side faces, turned toward the camera, lifting in the wind
    { ...SIDE_BANNER, at: ["band0"], yaw: BANNER_TURN },
    { ...SIDE_BANNER, at: ["band2"], yaw: -BANNER_TURN },
    // the alarm bell at the fence's gate, swinging in its frame
    { piece: "bellframe", at: ["corner3"], scale: BELL_SCALE },
    { piece: "bell", at: ["corner3"], offset: [0, BELL_PIVOT, 0], scale: BELL_SCALE, motion: { flap: { deg: 16, axis: "out", mirror: false, hz: 0.7 } } },
    // teal shields on the front face (over the bell) and the back
    { piece: "shield", at: ["wall3", "wall1"], offset: [0, 0.16, 0.004], scale: [1.9, 2.1, 2.3], fit: {
      arcane: { offset: [0, 0.12, 0.004], scale: [1.3, 1.4, 1.5] }, hex: { offset: [0, 0.11, 0.004], scale: [1.25, 1.35, 1.45] },
    } },
    { piece: "flare", at: ["apex"], offset: [0.05, 0.06, 0.05], scale: 1.5, detail: true, motion: { twinkle: 0.32, hz: 0.4 } },
  ],
  idle: {
    kind: "motes", colour: 0xffc070, every: [1.5, 2.4], at: "apex", lift: 0.05, count: 4, radius: 0.12,
    also: { kind: "glints", colour: 0x5fd2c2, count: 2, radius: 0.18 },
    fit: { frost: { at: "band0", lift: 0.0 }, lightning: { at: "band0", lift: 0.0 }, arcane: { at: "band0", lift: 0.0 }, hex: { at: "band0", lift: 0.0 } },
  },
  flourish: {
    impact: { kind: "sparks", colour: 0xffc070, count: 7 },
    impacts: [{ kind: "sparks", colour: 0xffc070, count: 7 }, { kind: "glint", colour: 0x5fd2c2, count: 6 }],
    muzzle: { kind: "sparks", colour: 0xffe2a8, count: 4 },
    charge: { colour: 0xffb060, count: 3 },
  },
  preview: { tower: "archer", tier: 3 },
};
